<template>
  <div>
    <AppInputLabel v-if="label" :name="name" :label="label" :hasError="hasError(name).value" :required="required" />

    <div class="relative">
      <div class="absolute inset-y-0 left-3 flex items-center">
        <slot name="prefix"></slot>
      </div>
      <input
        :id="name"
        class="w-full rounded-lg px-3 py-2"
        :class="[
          { 'border-critial': hasError(name).value },
          { 'pl-10': $slots.prefix }
        ]"
        :disabled="disabled"
        v-model="innerValue"
        :type="type"
        :placeholder="placeholder"
        @input="formatInput"
        :maxlength="maxlength ?? 1000"
        @keyup="validateInput"
        @blur="validateInput"
      />
    </div>
    <AppInputError v-show="hasError(name)" :error="errors.get(name)" />
  </div>
</template>
<script setup lang="ts">
import { inject, ref, watch, onBeforeUnmount } from "vue";
import { ValidationContext, ValidationContextKey } from "@/models/Context";
import useFormValidation from "@/modules/useFormValidation";
import AppInputLabel from "./AppInputLabel.vue";
import AppInputError from "./AppInputError.vue";


const props = defineProps<{
  required: boolean;
  name: string;
  label?: string;
  regex?: string;
  regexMessage?: string;
  value?: any;
  placeholder?: any;
  format?: string;
  type?: string;
  modelValue?: string | number | null;
  disabled?: boolean;
  maxlength?: number | string | null;
  noLabelBottomMargin?: boolean;
}>();

let innerValue = ref(props.modelValue);

const emit = defineEmits(["update:modelValue"]);

// Create a default validation context if none is provided
const defaultContext = {
  errors: new Map<string, string>(),
  validators: {} as Record<string, (key: string, validator: string) => void>,
  registerValidator: (key: string, validator: (key: string, validator: string) => void) => {
    defaultContext.validators[key] = validator;
  }
};

const context = inject(ValidationContextKey, defaultContext);
const { validateRequiredField, validateRequiredNumberField, validateRegexField, clearValidator, clearError, errors, registerValidator, hasError } = useFormValidation(context);
const control = ref();

const validateInput = () => {
  clearError(props.name);

  if (props.required) {
    if (props.type === "number") {
      validateRequiredNumberField(props.name, props.label || props.name, innerValue.value as number);
    } else {
      validateRequiredField(props.name, props.label || props.name, innerValue.value as string);
    }
  }

  if (props.regex) {
    if (!props.required && !innerValue.value) {
      errors.set(props.name, "");
      return;
    }
    validateRegexField(
      props.name,
      (innerValue.value ?? "") as string,
      props.regex,
      props.regexMessage ?? ""
    );
  }
};

registerValidator(props.name, validateInput);

watch(
  () => props.modelValue,
  (newVal) => {
    innerValue.value = newVal;
    validateInput();
  }
);

if (props.value && props.required) {
  validateInput();
}

const focus = () => {
  control.value.focus();
};

const formatInput = (event: Event) => {
  if (props.format) {
    const target = event.target as HTMLInputElement;
    let value = target.value.replace(/\D/g, ''); // Remove all non-digit characters

    let formattedValue = '';
    let formatIndex = 0;
    for (let i = 0; i < value.length; i++) {
      if (formatIndex >= props.format.length) break;
      if (props.format[formatIndex] === '#') {
        formattedValue += value[i];
        formatIndex++;
      } else {
        formattedValue += props.format[formatIndex];
        formatIndex++;
        i--; // Stay on the same digit
      }
    }
    innerValue.value = formattedValue;
  }
  emit("update:modelValue", innerValue.value);
};

onBeforeUnmount(() => {
  clearValidator(props.name);
  clearError(props.name);
});

defineExpose({ focus });
</script>
